/**
 * useOfferPriorityValidation Hook
 * 
 * Fetches existing offers for a user/partner combination and provides
 * priority validation helpers to show conflicts and suggest next available priority.
 */

import { useState, useEffect } from 'react';
import { offerService } from '@/app/services/offerService';
import type { OfferResponse } from '@/app/types/offers';
import { logger } from '@/app/utils/logger';

interface PriorityValidationResult {
  offers: OfferResponse[];
  usedPriorities: number[];
  isLoading: boolean;
  error: string | null;
  getNextAvailablePriority: () => number;
  isPriorityConflicting: (priority: number) => boolean;
  getConflictingOffers: (priority: number) => OfferResponse[];
}

export function useOfferPriorityValidation(
  userId: string | null,
  partnerProductId: number | null,
  excludeOfferId?: string
): PriorityValidationResult {
  const [offers, setOffers] = useState<OfferResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch if both userId and partnerProductId are provided
    if (!userId || !partnerProductId) {
      setOffers([]);
      setError(null);
      return;
    }

    const fetchOffers = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await offerService.queryOffers({
          user_id: userId,
          partner_product_id: partnerProductId,
          limit: 1000, // Get all offers for this user/partner
          offset: 0,
        });

        // Filter out the current offer if excludeOfferId is provided
        const filteredOffers = excludeOfferId
          ? result.offers.filter(o => o.offer_id !== excludeOfferId)
          : result.offers;

        setOffers(filteredOffers);
      } catch (err) {
        logger.error('Failed to fetch offers for priority validation', { 
          error: err, 
          userId, 
          partnerProductId 
        });
        setError('Failed to load existing offers');
        setOffers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOffers();
  }, [userId, partnerProductId, excludeOfferId]);

  // Get array of used priority numbers
  const usedPriorities = offers.map(o => o.display_priority).sort((a, b) => a - b);

  // Get next available priority (max + 1, or 1 if no offers)
  const getNextAvailablePriority = (): number => {
    if (offers.length === 0) return 1;
    const maxPriority = Math.max(...offers.map(o => o.display_priority));
    return maxPriority + 1;
  };

  // Check if a priority conflicts with existing offers
  const isPriorityConflicting = (priority: number): boolean => {
    return offers.some(o => o.display_priority === priority);
  };

  // Get offers that have the specified priority
  const getConflictingOffers = (priority: number): OfferResponse[] => {
    return offers.filter(o => o.display_priority === priority);
  };

  return {
    offers,
    usedPriorities,
    isLoading,
    error,
    getNextAvailablePriority,
    isPriorityConflicting,
    getConflictingOffers,
  };
}

export default useOfferPriorityValidation;

